import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@iconify/react";
import ServerTable from "./ServerTable";
import { DataTable, ErrorState, useReportContext } from "./ReportKit";
import { useReportData } from "./useReportData";
import { downloadCsv, formatValue } from "./reportUtils";

const FETCH_SIZE = 200; // the backend allows at most 200 rows per request

// A plain list of rows with a record count and a CSV button.
const LocalRows = ({ m, rows, note }) => (
  <>
    <div className="rp-modal-bar">
      <span>
        {rows.length} {rows.length === 1 ? "record" : "records"}.
        {note ? ` ${note}` : ""}
      </span>
      {!!rows.length && (
        <button
          type="button"
          className="rp-btn rp-btn-sm"
          onClick={() => downloadCsv(m.title, m.columns, rows)}
        >
          <Icon icon="mdi:download-outline" /> CSV
        </button>
      )}
    </div>
    <DataTable
      columns={m.columns}
      rows={rows}
      maxHeight="52vh"
      empty="Nothing matches yet."
    />
  </>
);

// For numbers the backend cannot filter itself: load the latest records and keep the matching ones.
const FilteredTable = ({ m }) => {
  const { data, error, reload } = useReportData(async () => {
    const res = await m.fetcher({
      ...m.params,
      page: 0,
      size: FETCH_SIZE,
      ...(m.sort ? { sort: m.sort } : {}),
    });
    const all = Array.isArray(res) ? res : res?.content || [];
    return { rows: all.filter(m.filter), capped: all.length >= FETCH_SIZE };
  }, [JSON.stringify(m.params || {}), m.title]);

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data) return <div className="rp-empty-inline">Loading…</div>;
  return (
    <LocalRows
      m={m}
      rows={data.rows}
      note={data.capped ? `Found among your latest ${FETCH_SIZE} records.` : ""}
    />
  );
};

/**
 * Pop-up that shows the records behind a number.
 * detail.modal: { title, how, columns, and ONE of:
 *   rows                      - rows we already have
 *   fetcher + params + filter - load latest records, keep those where filter(row) is true
 *   fetcher + params          - full paginated table }
 */
const KpiDetailsModal = ({ kpi, detail, currency, onClose }) => {
  const { periodText } = useReportContext();
  const m = detail.modal;
  const closeRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onCloseRef.current();
    };
    document.addEventListener("keydown", onKey);
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const how = String(m.how || "").replace("{p}", periodText);

  return createPortal(
    <div
      className="reports-page rp-modal-host"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="rp-modal"
        role="dialog"
        aria-modal="true"
        aria-label={m.title}
      >
        <header className="rp-modal-head">
          <div className="rp-modal-kpi">
            <b>{formatValue(kpi.value, kpi.unit, currency)}</b>
            <div>
              <h3>{m.title}</h3>
              {how && <p>{how}</p>}
            </div>
          </div>
          <button
            ref={closeRef}
            type="button"
            className="rp-modal-close"
            aria-label="Close"
            onClick={onClose}
          >
            <Icon icon="mdi:close" />
          </button>
        </header>
        <div className="rp-modal-body">
          {m.rows ? (
            <LocalRows m={m} rows={m.rows} note={m.note} />
          ) : m.filter ? (
            <FilteredTable m={m} />
          ) : (
            <ServerTable
              bare
              hideTitle
              title={m.title}
              columns={m.columns}
              fetcher={m.fetcher}
              params={m.params || {}}
              defaultSort={m.sort}
              searchable={!!m.searchable}
              sortable={!!m.sort}
              pageSize={10}
              exportName={m.title}
            />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default KpiDetailsModal;
